import { useEffect, useState } from "react";
import { listPayments, recordPayment } from "../api/finance.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Payment {
  id: string;
  invoiceId: string;
  clientId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  reference: string | null;
  status: string;
  paidAt: string;
}

const METHOD_LABEL: Record<string, string> = {
  BANK_TRANSFER: "Bank Transfer",
  CREDIT_CARD: "Credit Card",
  UPI: "UPI",
  CHEQUE: "Cheque",
  CASH: "Cash",
  OTHER: "Other",
};

function currency(n: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function FinancePaymentsPage() {
  const { token } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    invoiceId: "",
    amount: "",
    paymentMethod: "BANK_TRANSFER",
    reference: "",
    paidAt: "",
  });
  const [recording, setRecording] = useState(false);

  const reload = () => {
    void listPayments(token).then((d) => {
      setPayments((d as { items: Payment[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const record = async () => {
    if (!form.invoiceId || !form.amount) return;
    if (
      !window.confirm(
        "Record this payment? This action requires human approval and is irreversible.",
      )
    )
      return;
    setRecording(true);
    await recordPayment(token, {
      invoiceId: form.invoiceId,
      amount: parseFloat(form.amount),
      paymentMethod: form.paymentMethod,
      reference: form.reference || undefined,
      paidAt: form.paidAt || undefined,
    });
    setShowForm(false);
    setRecording(false);
    setForm({
      invoiceId: "",
      amount: "",
      paymentMethod: "BANK_TRANSFER",
      reference: "",
      paidAt: "",
    });
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Payments</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
          >
            {showForm ? "Cancel" : "Record Payment"}
          </button>
        </div>
        <div className="mb-6 rounded-lg border border-yellow-800 bg-yellow-950/30 p-3 text-sm text-yellow-300">
          Human approval required. All payment transactions are permanently recorded and auditable.
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.invoiceId}
                onChange={(e) => setForm((f) => ({ ...f, invoiceId: e.target.value }))}
                placeholder="Invoice ID"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="Amount (₹)"
                type="number"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300"
              >
                {Object.entries(METHOD_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <input
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                placeholder="Reference / UTR / Cheque #"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.paidAt}
                onChange={(e) => setForm((f) => ({ ...f, paidAt: e.target.value }))}
                type="date"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
              />
            </div>
            <button
              onClick={() => void record()}
              disabled={recording}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {recording ? "Recording..." : "Record Payment"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {payments.length === 0 && <p className="text-gray-400">No payments recorded.</p>}
            {payments.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 p-3"
              >
                <span className="text-lg font-bold text-green-400">{currency(p.amount)}</span>
                <span className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-300">
                  {METHOD_LABEL[p.paymentMethod] ?? p.paymentMethod}
                </span>
                {p.reference && (
                  <span className="font-mono text-xs text-gray-500">{p.reference}</span>
                )}
                <span className="ml-auto text-xs text-gray-600">
                  {new Date(p.paidAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
